import AccountType from 'scripts/constants/AccountType';
import { roundTransactionFee } from 'scripts/utilities/helpers';
import moment from 'moment';
import { TAX_STATEMENT_AVAILABLE_DATES } from 'src/settings/dates';
import PropertyTypes from 'src/enums/propertyTypes';
import InvestmentTypes from 'src/enums/investmentTypes';

export const normalizeAdvisorDate = (value) =>
  typeof value === 'string' ? value.replace(/\[[^\]]+\]$/, '') : value;

export const getAdvisorDateTimestamp = (value) => {
  const timestamp = new Date(normalizeAdvisorDate(value) || 0).getTime();
  return Number.isFinite(timestamp) ? timestamp : 0;
};

export const getDocumentFinancialYears = (transactions) => {
  const activityYears = transactions.reduce((years, transaction) => {
    if (transaction.cancelled) return years;
    const distributionPeriod = (transaction.description || '').match(
      /^Distribution Payment for ([A-Za-z]+ \d{4})/i,
    );
    const activityDate = distributionPeriod
      ? moment(distributionPeriod[1], 'MMMM YYYY', true)
      : moment(
          normalizeAdvisorDate(transaction.date) || '',
          moment.ISO_8601,
          true,
        ).utcOffset(600);

    if (activityDate.isValid()) {
      years.add(activityDate.year() + (activityDate.month() >= 6 ? 1 : 0));
    }
    return years;
  }, new Set());

  return TAX_STATEMENT_AVAILABLE_DATES.filter((year) =>
    activityYears.has(year),
  );
};

const ACCOUNT_TYPE_LABELS = {
  [AccountType.INDIVIDUAL]: 'Individual',
  [AccountType.MINOR]: 'Minor',
  [AccountType.SMSF]: 'SMSF',
  [AccountType.TRUST]: 'Trust',
  [AccountType.CORPORATION]: 'Company',
  [AccountType.INTERNAL]: 'Internal',
  [AccountType.JOINT]: 'Joint',
  [AccountType.DECEASED]: 'Deceased',
  [AccountType.PARTNERSHIP]: 'Partnership',
  [AccountType.GOVERNMENT]: 'Government',
  [AccountType.OTHERNONINDIVIDUAL]: 'Other',
};

const numberOrNull = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};

const sumNumbers = (values) =>
  values.reduce((total, value) => total + Number(value || 0), 0);

const sumKnownNumbers = (values) =>
  values.every((value) => value !== null && value !== undefined)
    ? sumNumbers(values)
    : null;

const normalizeText = (value) =>
  value === null || value === undefined || value === '' ? null : String(value);

const buildFullName = (profile) =>
  [profile.givenName, profile.middleName, profile.familyName]
    .filter(Boolean)
    .join(' ') ||
  profile.email ||
  'Client';

const getInitials = (name) =>
  String(name || '')
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join('');

const getAccountTypeLabel = (accountType) =>
  ACCOUNT_TYPE_LABELS[String(accountType || '').toUpperCase()] ||
  normalizeText(accountType);

const getLocation = (holding) =>
  [holding.propertySuburb, holding.propertyState].filter(Boolean).join(', ');

const getPlans = (userAccount) =>
  Array.isArray(userAccount.plans) ? userAccount.plans : [];

const getHoldings = (portfolio) =>
  Array.isArray(portfolio.records) ? portfolio.records : [];

const getWalletTransactions = (plans) =>
  plans
    .reduce((transactions, plan) => {
      const walletTransactions =
        plan.wallet && Array.isArray(plan.wallet.transactions)
          ? plan.wallet.transactions
          : [];
      const planType =
        plan.planSubscription && plan.planSubscription.planType
          ? plan.planSubscription.planType
          : null;

      return transactions.concat(
        walletTransactions.map((transaction) => ({
          ...transaction,
          planType,
        })),
      );
    }, [])
    .sort(
      (left, right) =>
        getAdvisorDateTimestamp(right.created) -
        getAdvisorDateTimestamp(left.created),
    );

const normalizeHolding = (holding) => ({
  propertyCode: normalizeText(holding.propertyCode),
  location: getLocation(holding) || null,
  state: normalizeText(holding.propertyState),
  dateAcquired: normalizeText(holding.dateAcquired),
  bricks: numberOrNull(holding.totalBricks),
  averageCostPerBrick: numberOrNull(holding.costBasis),
  brickValue: numberOrNull(holding.marketBrickValue),
  investment: numberOrNull(holding.amountInvested),
  portfolioValue: numberOrNull(holding.totalBrickValue),
  distributions: numberOrNull(holding.distributionsPaid),
  roi: numberOrNull(holding.totalRoi),
  capitalReturns: numberOrNull(holding.capitalReturns),
  brickPriceType: normalizeText(holding.brickPriceType),
});

const normalizeTransaction = (transaction) => ({
  id: normalizeText(transaction.id),
  date: normalizeText(transaction.created),
  description: normalizeText(transaction.description),
  amount: numberOrNull(transaction.amount),
  externalId: normalizeText(transaction.externalId),
  planType: normalizeText(transaction.planType),
  cancelled: Boolean(transaction.cancelled),
});

const normalizeOrder = (order) => {
  const side = normalizeText(order.orderSide);
  const commission = numberOrNull(order.commission);
  const quantity = numberOrNull(order.quantity);
  const price = numberOrNull(order.price);
  const inclusiveTotal = numberOrNull(order.totalPriceInclFees);
  const totalPrice = numberOrNull(order.totalPrice);
  const total =
    totalPrice !== null
      ? totalPrice
      : quantity !== null && price !== null
        ? quantity * price
        : null;
  const fee =
    total !== null && commission !== null
      ? roundTransactionFee.call(total * commission)
      : null;
  let executionAmount = null;
  if (side === 'sell' && total !== null && fee !== null) {
    executionAmount = total - fee;
  } else if (side === 'buy' || side === 'pre') {
    executionAmount =
      inclusiveTotal !== null
        ? inclusiveTotal
        : total !== null && fee !== null
          ? total + fee
          : null;
  }

  return {
    id: normalizeText(order.orderId),
    propertyCode: normalizeText(order.propertyCode),
    side: side === 'buy' && commission === 0 ? 'pre' : side,
    rawStatus: normalizeText(order.status),
    date: normalizeText(order.date),
    quantity,
    price,
    commission,
    total,
    executionAmount,
    totalWithFees:
      inclusiveTotal !== null
        ? inclusiveTotal
        : totalPrice !== null
          ? totalPrice
          : null,
  };
};

export const getSignedOrderAmount = (order) => {
  if (!Number.isFinite(order.executionAmount)) return null;
  if (order.executionAmount === 0) return 0;
  if (order.side === 'sell') return order.executionAmount;
  if (order.side === 'buy' || order.side === 'pre') {
    return -order.executionAmount;
  }
  return null;
};

const normalizePlan = (plan) => {
  const subscription = plan.planSubscription || {};
  const portfolio = plan.portfolio || {};
  const wallet = plan.wallet || {};

  return {
    id: normalizeText(subscription.planSubscriptionId),
    type: normalizeText(subscription.planType),
    createdAt: normalizeText(subscription.createdAt),
    cancelledAt: normalizeText(subscription.cancelledAt),
    enabled: Boolean(subscription.enabled),
    totalAccountValue: numberOrNull(plan.totalAccountValue),
    availableBalance: numberOrNull(plan.availableToTradeBalance),
    blockedAmount: numberOrNull(plan.blockedAmount),
    preorderBricks: numberOrNull(plan.nbOfBricksPreorders),
    preorderValue: numberOrNull(plan.preOrderValue),
    preorderValueWithFees: numberOrNull(plan.preOrderValueInclFees),
    walletBalance: numberOrNull(wallet.balance),
    portfolioValue: numberOrNull(portfolio.totalValue),
    amountInvested: numberOrNull(portfolio.amountInvested),
    distributions: numberOrNull(portfolio.distributionsPaid),
    totalRoi: numberOrNull(portfolio.totalRoi),
    properties: numberOrNull(portfolio.totalPropertiesOwned),
    bricks: numberOrNull(portfolio.totalBricksOwned),
    holdings: getHoldings(portfolio).map(normalizeHolding),
  };
};

const normalizeSmsfAccount = (details) =>
  details
    ? {
        entityName: normalizeText(details.entityName),
        trusteeType: normalizeText(details.trusteeType),
        abn: normalizeText(details.abn),
        address: normalizeText(details.address),
        suburb: normalizeText(details.suburb),
        state: normalizeText(details.state),
        postcode: normalizeText(details.postcode),
        acn: normalizeText(details.acn),
        corporateTrusteeName: normalizeText(details.corpTrusteeName),
        corporateTrusteeAddress: normalizeText(details.corpTrusteeAddress),
        directorOfCorporateTrustee:
          details.isDirectorOfCorpTrustee === null ||
          details.isDirectorOfCorpTrustee === undefined
            ? null
            : Boolean(details.isDirectorOfCorpTrustee),
      }
    : null;

const normalizeMinorAccount = (details) =>
  details
    ? {
        trustName: normalizeText(details.trustName),
        givenName: normalizeText(details.firstName),
        middleName: normalizeText(details.middleName),
        familyName: normalizeText(details.lastName),
        dateOfBirth: normalizeText(details.dateOfBirth),
        relationship: normalizeText(details.relation),
      }
    : null;

const normalizeWholesaleInvestor = (details) =>
  details
    ? {
        verified: Boolean(details.verified),
        certificateAvailable: Boolean(details.certificateLink),
        certificateExpiry: normalizeText(details.certificateExpired),
      }
    : null;

const normalizeBeneficialOwner = (owner) => ({
  name:
    [owner.givenName, owner.middleName, owner.familyName]
      .filter(Boolean)
      .join(' ') || null,
  dateOfBirth: normalizeText(owner.dateOfBirth),
  address: normalizeText(owner.fullAddress),
  identityChecked: Boolean(owner.idChecked),
  trustee: Boolean(owner.isTrustee),
  director: Boolean(owner.isDirector),
  shareholder: Boolean(owner.isShareholder),
  beneficiary: Boolean(owner.isBeneficiary),
});

const getStatus = (profile) =>
  normalizeText(profile.deactivatedAt) ? 'Inactive' : 'Active';

export const normalizeAdvisorClientDetail = (client = {}, ordersValue) => {
  const userAccount = client.userAccount || {};
  const userTransactions = client.userTransactions || {};
  const profile = client.userProfile || {};
  const relationship = client.relationship || {};
  const portfolio = userAccount.aggregatedPortfolio || {};
  const plans = getPlans(userAccount);
  const name = buildFullName(profile);
  const holdings = getHoldings(portfolio)
    .map(normalizeHolding)
    .sort(
      (left, right) =>
        Number(right.portfolioValue || 0) - Number(left.portfolioValue || 0),
    );
  const transactions = getWalletTransactions(plans)
    .map(normalizeTransaction)
    .filter((transaction) => !transaction.cancelled);
  const orders = Array.isArray(ordersValue)
    ? ordersValue.map(normalizeOrder)
    : null;
  const normalizedPlans = plans.map(normalizePlan);

  return {
    identity: {
      id: normalizeText(profile.id),
      name,
      initials: getInitials(name),
      email: normalizeText(profile.email),
      mobile: normalizeText(profile.mobilePhone),
      accountType: normalizeText(profile.accountType),
      accountTypeLabel: getAccountTypeLabel(profile.accountType),
      dateOfBirth: normalizeText(profile.dateOfBirth),
      age: numberOrNull(profile.age),
      address: normalizeText(profile.address),
      clientSince: normalizeText(relationship.updatedAt),
      lastLogin: normalizeText(profile.lastLoggedInAt),
      status: normalizeText(getStatus(profile)),
    },
    account: {
      displayName: normalizeText(userAccount.displayName),
      legalName: normalizeText(userAccount.legalName),
      totalValue: numberOrNull(userAccount.totalAccountsValue),
      totalBricksOwned: numberOrNull(userAccount.totalBricksOwned),
      preorderBricks: numberOrNull(userAccount.totalBricksPreOrdered),
      propertiesInvestedIn: numberOrNull(userAccount.totalPropertiesInvestedIn),
      totalDeposits: numberOrNull(userTransactions.totalDeposits),
      totalWithdrawals: numberOrNull(userTransactions.totalWithdrawals),
    },
    compliance: {
      onboarding: normalizeText(profile.nextRegistrationStep),
      identityCheck: normalizeText(profile.identityCheck),
      accountStatus: normalizeText(profile.accountStatus),
      australianTaxResidency: normalizeText(profile.australianTaxResidency),
      usCitizen: normalizeText(profile.usCitizen),
    },
    relationship: {
      status: normalizeText(relationship.status),
      createdAt: normalizeText(relationship.createdAt),
      updatedAt: normalizeText(relationship.updatedAt),
    },
    specialAccounts: {
      smsf: normalizeSmsfAccount(client.smsfAccountDetails),
      minor: normalizeMinorAccount(client.minorAccountDetails),
      wholesale: normalizeWholesaleInvestor(client.wholesaleInvestor),
      beneficialOwners: Array.isArray(client.beneficialOwnerDetails)
        ? client.beneficialOwnerDetails.map(normalizeBeneficialOwner)
        : [],
    },
    financials: {
      portfolioValue: numberOrNull(portfolio.totalValue),
      availableBalance: sumKnownNumbers(
        plans.map((plan) => numberOrNull(plan.availableToTradeBalance)),
      ),
      totalRoi: numberOrNull(portfolio.totalRoi),
      distributions: numberOrNull(portfolio.distributionsPaid),
      amountInvested: numberOrNull(portfolio.amountInvested),
      properties:
        numberOrNull(portfolio.totalPropertiesOwned) !== null
          ? numberOrNull(portfolio.totalPropertiesOwned)
          : holdings.length,
      bricks:
        numberOrNull(portfolio.totalBricksOwned) !== null
          ? numberOrNull(portfolio.totalBricksOwned)
          : numberOrNull(userAccount.totalBricksOwned),
    },
    holdings,
    transactions,
    plans: normalizedPlans,
    orders,
    pendingOrderCount: orders ? orders.length : null,
    pendingOrderValue: orders
      ? sumNumbers(orders.map((order) => order.total))
      : null,
    pendingOrderBricks: orders
      ? sumNumbers(orders.map((order) => order.quantity))
      : null,
    pendingSellBricks: orders
      ? sumKnownNumbers(
          orders
            .filter((order) => order.side === 'sell')
            .map((order) => order.quantity),
        )
      : null,
    pendingFundsHeld: orders
      ? sumKnownNumbers(
          orders
            .filter((order) => ['buy', 'pre'].includes(order.side))
            .map((order) => order.executionAmount),
        )
      : null,
    documents: {
      hasPortfolioStatement: true,
      hasWholesaleCertificate: Boolean(
        client.wholesaleInvestor && client.wholesaleInvestor.certificateLink,
      ),
    },
  };
};

export const getPortfolioOwnership = (bricks, totalBricks) => {
  const owned = numberOrNull(bricks);
  const issued = numberOrNull(totalBricks);
  return owned !== null && owned >= 0 && issued !== null && issued > 0
    ? owned / issued
    : null;
};

export const getPortfolioInvestmentType = (property) => {
  if (!property) return null;
  if (
    property.propertyType === PropertyTypes.development ||
    property.investmentType === 'development'
  ) {
    return 'development';
  }
  return Object.values(InvestmentTypes).includes(property.investmentType)
    ? property.investmentType
    : null;
};

export const getPortfolioHoldingsForPlan = (
  holdings,
  plans,
  planType = 'all',
) => {
  if (planType === 'all') return holdings;
  const grouped = new Map();
  plans
    .filter((plan) => plan.type === planType)
    .forEach((plan) => {
      (plan.holdings || []).forEach((holding) => {
        const records = grouped.get(holding.propertyCode) || [];
        grouped.set(holding.propertyCode, [...records, holding]);
      });
    });
  return Array.from(grouped.values()).map((records) => {
    if (records.length === 1) return records[0];
    const sum = (key) => sumKnownNumbers(records.map((record) => record[key]));
    const bricks = sum('bricks');
    const investment = sum('investment');
    const weightedAverage = (key, weightKey, total) => {
      const weighted = sumKnownNumbers(
        records.map((record) =>
          !Number.isFinite(record[key]) || !Number.isFinite(record[weightKey])
            ? null
            : record[key] * record[weightKey],
        ),
      );
      return weighted !== null && total > 0 ? weighted / total : null;
    };
    return {
      ...records[0],
      bricks,
      investment,
      portfolioValue: sum('portfolioValue'),
      distributions: sum('distributions'),
      averageCostPerBrick: weightedAverage(
        'averageCostPerBrick',
        'bricks',
        bricks,
      ),
      roi: weightedAverage('roi', 'investment', investment),
      capitalReturns: weightedAverage(
        'capitalReturns',
        'investment',
        investment,
      ),
    };
  });
};

export const getFilteredPortfolioHoldings = (
  holdings,
  {
    search = '',
    state = 'all',
    investmentType = 'all',
    sortBy = 'propertyCode',
    sortDirection = 'asc',
  } = {},
) => {
  const normalizedSearch = search.trim().toLowerCase();
  const compareCodes = (left, right) =>
    String(left.propertyCode || '').localeCompare(
      String(right.propertyCode || ''),
    );

  return holdings
    .filter((holding) => state === 'all' || holding.state === state)
    .filter(
      (holding) =>
        investmentType === 'all' || holding.investmentType === investmentType,
    )
    .filter((holding) =>
      [holding.propertyCode, holding.location, holding.state].some((value) =>
        String(value || '')
          .toLowerCase()
          .includes(normalizedSearch),
      ),
    )
    .sort((left, right) => {
      const leftValue = left[sortBy];
      const rightValue = right[sortBy];
      if (leftValue === null || leftValue === undefined) {
        return rightValue === null || rightValue === undefined
          ? compareCodes(left, right)
          : 1;
      }
      if (rightValue === null || rightValue === undefined) return -1;
      const comparison =
        typeof leftValue === 'number' && typeof rightValue === 'number'
          ? leftValue - rightValue
          : String(leftValue).localeCompare(String(rightValue));
      return (
        comparison * (sortDirection === 'desc' ? -1 : 1) ||
        compareCodes(left, right)
      );
    });
};

export const getPortfolioComposition = (holdings, maximumItems = 4) => {
  const valuedHoldings = holdings.filter(
    (holding) => Number(holding.portfolioValue) > 0,
  );
  const totalValue = sumNumbers(
    valuedHoldings.map((holding) => holding.portfolioValue),
  );
  if (!totalValue) return [];

  const leadingHoldings = valuedHoldings.slice(0, maximumItems);
  const remainingHoldings = valuedHoldings.slice(maximumItems);
  const composition = leadingHoldings.map((holding) => ({
    key: holding.propertyCode,
    label: holding.location || holding.propertyCode,
    propertyCode: holding.propertyCode,
    value: holding.portfolioValue,
    percentage: holding.portfolioValue / totalValue,
  }));
  const otherValue = sumNumbers(
    remainingHoldings.map((holding) => holding.portfolioValue),
  );

  if (otherValue > 0) {
    composition.push({
      key: 'other',
      label: 'Other',
      propertyCode: null,
      value: otherValue,
      percentage: otherValue / totalValue,
    });
  }

  return composition;
};

export const getPortfolioPerformance = (holdings) => {
  const holdingsWithRoi = holdings.filter((holding) => holding.roi !== null);
  const byRoi = holdingsWithRoi
    .slice()
    .sort((left, right) => left.roi - right.roi);
  const totalInvestment = sumNumbers(
    holdingsWithRoi.map((holding) => holding.investment),
  );
  const weightedRoi = totalInvestment
    ? holdingsWithRoi.reduce(
        (total, holding) =>
          total + Number(holding.roi || 0) * Number(holding.investment || 0),
        0,
      ) / totalInvestment
    : null;

  return {
    best: byRoi.length ? byRoi[byRoi.length - 1] : null,
    worst: byRoi.length ? byRoi[0] : null,
    weightedRoi,
  };
};

export const getPortfolioConcentration = (holdings) => {
  const totalValue = sumNumbers(
    holdings.map((holding) => holding.portfolioValue),
  );
  if (!totalValue) {
    return { largestHolding: null, topThree: null };
  }

  return {
    largestHolding:
      Number(holdings[0] && holdings[0].portfolioValue) / totalValue,
    topThree:
      sumNumbers(
        holdings.slice(0, 3).map((holding) => holding.portfolioValue),
      ) / totalValue,
  };
};
